import React from 'react';
import { useAudioPlayer } from '../contexts/AudioPlayerContext';
import './QueuePanel.css';

interface QueuePanelProps {
  isOpen: boolean;
  onClose: () => void;
}

const QueuePanel: React.FC<QueuePanelProps> = ({ isOpen, onClose }) => {
  const {
    queue,
    queueIndex,
    currentStream,
    isPlaying,
    skipTo,
    removeFromQueue,
    clearQueue,
  } = useAudioPlayer();

  if (!isOpen) return null;

  const upcomingTracks = queue.slice(queueIndex + 1);

  return (
    <div className="queue-panel">
      <div className="queue-header">
        <h3 className="queue-title">Queue</h3>
        <div className="queue-actions">
          {queue.length > 1 && (
            <button className="queue-clear-btn" onClick={clearQueue}>
              Clear
            </button>
          )}
          <button className="queue-close-btn" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>
      </div>

      <div className="queue-content">
        {/* Now Playing */}
        {currentStream && (
          <div className="queue-section">
            <div className="queue-section-title">Now Playing</div>
            <div className="queue-item queue-item-current">
              <div className="queue-item-indicator">
                {isPlaying ? (
                  <div className="queue-item-playing">
                    <span></span><span></span><span></span>
                  </div>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
                  </svg>
                )}
              </div>
              {currentStream.coverImage ? (
                <img src={currentStream.coverImage} alt="" className="queue-item-cover" />
              ) : (
                <div className="queue-item-cover queue-item-cover-placeholder">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/>
                  </svg>
                </div>
              )}
              <div className="queue-item-info">
                <div className="queue-item-name">{currentStream.name}</div>
                {currentStream.uploaderDisplayName && (
                  <div className="queue-item-artist">{currentStream.uploaderDisplayName}</div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Up Next */}
        {upcomingTracks.length > 0 && (
          <div className="queue-section">
            <div className="queue-section-title">Up Next ({upcomingTracks.length})</div>
            <div className="queue-list">
              {upcomingTracks.map((stream, index) => {
                const actualIndex = queueIndex + 1 + index;
                return (
                  <div
                    key={stream.streamId}
                    className="queue-item"
                    onClick={() => skipTo(actualIndex)}
                  >
                    <div className="queue-item-index">{index + 1}</div>
                    {stream.coverImage ? (
                      <img src={stream.coverImage} alt="" className="queue-item-cover" />
                    ) : (
                      <div className="queue-item-cover queue-item-cover-placeholder">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/>
                        </svg>
                      </div>
                    )}
                    <div className="queue-item-info">
                      <div className="queue-item-name">{stream.name}</div>
                      {stream.uploaderDisplayName && (
                        <div className="queue-item-artist">{stream.uploaderDisplayName}</div>
                      )}
                    </div>
                    <button
                      className="queue-item-remove"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFromQueue(actualIndex);
                      }}
                      title="Remove from queue"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                      </svg>
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Empty State */}
        {queue.length === 0 && (
          <div className="queue-empty">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor" opacity="0.3">
              <path d="M15 6H3v2h12V6zm0 4H3v2h12v-2zM3 16h8v-2H3v2zM17 6v8.18c-.31-.11-.65-.18-1-.18-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3V8h3V6h-5z"/>
            </svg>
            <p>Your queue is empty</p>
            <p className="queue-empty-hint">Play a stream or add streams to your queue</p>
          </div>
        )}

        {/* Only current track, no upcoming */}
        {queue.length === 1 && currentStream && (
          <div className="queue-empty-upcoming">
            <p>No upcoming tracks</p>
            <p className="queue-empty-hint">Add more streams to your queue</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default QueuePanel;
